import { FC, useEffect, useState } from 'react';
import { SelectAutocomplete, IAutoSelectOption } from 'team-hero-ui';
import { useTranslation } from 'react-i18next';

import type { IOption } from 'components/Modals/AddEditModal/modalConfig/modalConfig.interface';
import type {
  IOptionGroupWithItems,
  ISelectInputFieldProps,
} from './SelectInput.types';
import { useGetSelectExternalOptionValues } from 'hooks/getSelectExternalOptionValues/getSelectExternalOptionValues.hook';
import { createOptionSelectGroups } from '../SelectField/helper/selectOptions.helper';
import ValidationWrapper from 'components/Modals/AddEditModal/ModalValidationWrapper';

const defaultArray: [] = [];

const SelectInput: FC<ISelectInputFieldProps> = ({
  styleType = 'modal',
  label,
  value,
  onChange,
  externalDataSource,
  validationText,
  options,
  helperText,
  enableEmptySelect = true,
  disabled = false,
  disableValidation = false,
  inputRef,
  optionGroups = defaultArray,
  hideValidationBorder = false,
}) => {
  const [items, setItems] = useState<IOption[]>([]);
  const { t } = useTranslation('general');
  const { data: externalDataValues } =
    useGetSelectExternalOptionValues(externalDataSource);

  useEffect(() => {
    if (options?.length) {
      const selectOptions = options?.map((option) => ({
        label: option.label,
        value: option.value,
      }));

      setItems(selectOptions);
    }
  }, [options]);

  const selectItems = externalDataSource ? externalDataValues : items;

  const selectItemList = (): IAutoSelectOption[] => {
    // if optionGroups are defined, return the grouped selectItems
    // not grouped items, will be put to others group
    if (optionGroups.length > 0) {
      const optionSelectGroups = createOptionSelectGroups(
        optionGroups,
        selectItems,
        t
      );
      const optionsReturn: IAutoSelectOption[] = [];
      optionSelectGroups.forEach((optionGroup: IOptionGroupWithItems) => {
        if (optionGroup.values.length === 0) return;
        optionsReturn.push({
          value: `group-divider_${optionGroup.label}`,
          label: optionGroup.label,
        });
        optionGroup.values.forEach((item) => {
          optionsReturn.push({ value: item.value, label: item.label });
        });
      });
      return optionsReturn;
    }
    // if no optionGroups are defined, return the default selectItems
    return selectItems.map((item) => {
      return { value: item.value, label: item.label };
    });
  };

  const selectRender = () => (
    <SelectAutocomplete
      styleType={styleType}
      disabled={disabled}
      label={label}
      value={value}
      onChange={(_, selectedInput) => onChange(selectedInput.value as string)}
      helperText={helperText}
      inputRef={
        inputRef as (
          ref:
            | HTMLSelectElement
            | { node: HTMLInputElement; value: string | number | undefined }
        ) => void
      }
      options={
        enableEmptySelect
          ? [...selectItemList(), { value: '', label: t('select.noValue') }]
          : selectItemList()
      }
    />
  );

  if (disableValidation) {
    return selectRender();
  }

  return (
    <ValidationWrapper hideBorder={hideValidationBorder} text={validationText}>
      {selectRender()}
    </ValidationWrapper>
  );
};

export default SelectInput;
